import { BUILD_SLOTS_PER_CORE } from '../data/buildings.js';
import { DISRUPT_UPLINK_TARGET_RANGE_SQ } from '../data/disruptUplink.js';
import { FORTIFY_RELAY_TARGET_RANGE_SQ } from '../data/fortifyRelay.js';
import { FORWARD_TURRET_RANGE_SQ } from '../data/forwardTurret.js';
import { SQUAD_DEFS } from '../data/squads.js';
import type { PlayerView } from '../matchHost/MatchHost.js';
import { isSlotOccupied } from '../simulation/buildSlots.js';
import type { Building } from '../simulation/entities/Building.js';
import type { ControlNode } from '../simulation/entities/ControlNode.js';
import type { Core } from '../simulation/entities/Core.js';
import type { Squad, SquadArchetype } from '../simulation/entities/Squad.js';
import type { EntityId, PlayerId } from '../simulation/ids.js';
import { stableHash } from '../utils/rng.js';

// Shared bot helpers: pure read-only utilities over PlayerView. Recruit, Tactical,
// and Commander all share the same CommandSource shape, the same snapshot-buffer
// pattern, and the same set of geometric primitives — these are extracted here so
// the BotPlayer implementations don't duplicate (Rule 13: no shims, but factoring
// out genuine duplication is allowed).
//
// Nothing here mutates state. Callers must still go through the Command pipe
// (Rule 6).

export interface DesiredOrder {
  kind: 'move' | 'attack_move';
  target: { x: number; y: number };
}

// Two desired orders are treated as equal when their targets are within this many
// pixels — keeps the bot from spamming A*-replans for a 1-px drift.
export const TARGET_DEDUPE_EPSILON_PX = 16;

// Map midpoint used for the Tactical/Commander defend anchor. Foundry is 2000×2000;
// midpoint at (1000, 1000) is the canonical center.
export const MAP_MIDPOINT = { x: 1000, y: 1000 } as const;

// Skirmish range used by Mark Target: Recon paints if there's an Assault ally
// within this radius of the target — the Assault is then close enough that the
// +10% damage multiplier matters within the 8 s mark window. Used by Tactical;
// Commander uses a similar threshold but picks by maxHp instead of current HP.
export const ASSAULT_SKIRMISH_PX = 350;

// Phase Shield trigger: any ally squad within shieldRadius of the Shield with
// hp/maxHp below this threshold. Mirrors docs/02_GAME_DESIGN.md §2 "ally < 50%".
export const SHIELD_HP_TRIGGER_RATIO = 0.5;

// Avoid known enemy turrets by this distance when picking a Recon target. 400 px
// matches the Turret range (BUILDINGS.turret.range), so Recon won't waltz into
// turret fire.
export const TURRET_AVOID_PX = 400;

// V1.5+ telemetry pre-flight: deterministic-per-seed but varying-across-seeds
// jitter applied in pickRelayTarget. Without this, identical Tactical-vs-Tactical
// snapshots from different state.seed values produce identical relay picks
// (since no system reads state.rng), so playtest-auto.ts gets effective n=1.
//
// The smallest existing score bump in pickRelayTarget is the per-archetype
// owner bonus (+100_000 for "we already own this relay"). The largest is the
// Recon-near-turret penalty (+5_000_000). 1000 sits well below both — it
// perturbs ties within the same scoring tier (e.g., two same-owner relays
// equidistant from the squad) without ever overriding the "this turret is
// live, don't go near it" or "we already own this relay" logic.
//
// Do NOT raise this without a corresponding test that the new value still
// doesn't override the live-turret penalty (see botJitter.test.ts).
export const PICK_RELAY_JITTER_MAGNITUDE = 1000;

export function findCoreOf(snap: PlayerView, playerId: PlayerId): Core | null {
  for (const core of Object.values(snap.cores)) {
    if (core.ownerId === playerId) return core;
  }
  return null;
}

export function nearestNeutralRelay(
  snap: PlayerView,
  from: { x: number; y: number },
): { x: number; y: number } | null {
  let best: ControlNode | null = null;
  let bestSq = Infinity;
  for (const node of Object.values(snap.controlNodes)) {
    if (node.ownerId !== null) continue;
    const dSq = sqDist(node.pos, from);
    if (dSq < bestSq) {
      bestSq = dSq;
      best = node;
    }
  }
  return best === null ? null : { x: best.pos.x, y: best.pos.y };
}

export function nudgeToward(
  from: { x: number; y: number },
  toward: { x: number; y: number },
  distancePx: number,
): { x: number; y: number } {
  const dx = toward.x - from.x;
  const dy = toward.y - from.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) return { x: from.x, y: from.y };
  const inv = distancePx / len;
  return { x: from.x + dx * inv, y: from.y + dy * inv };
}

export function ordersMatch(a: DesiredOrder, b: DesiredOrder): boolean {
  if (a.kind !== b.kind) return false;
  return Math.hypot(a.target.x - b.target.x, a.target.y - b.target.y) <= TARGET_DEDUPE_EPSILON_PX;
}

export function sqDist(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

// Push-army-value heuristic uses squad cost as a coarse "tier" weight, scaled by
// current HP fraction so a one-HP unit doesn't inflate the score. Tactical and
// Commander both use this for the push/defend value comparison.
export function squadValue(sq: Squad): number {
  const def = SQUAD_DEFS[sq.archetype];
  return def.cost * (sq.maxHp <= 0 ? 0 : sq.hp / sq.maxHp);
}

// Combat micro — wounded-retreat threshold. A squad below this HP fraction is
// considered a candidate for pull-back; the helper still gates on whether a
// retreat anchor (Shield bubble, Core) is meaningfully closer than re-engaging.
// 0.30 is loose enough that we don't yo-yo (a squad doesn't retreat after a
// single graze) and tight enough that a real loss-trade pulls back before
// dying. Recon retreats earlier — see pickRetreatTarget for the per-archetype
// scaling.
export const RETREAT_HP_RATIO = 0.30;
export const RECON_RETREAT_HP_RATIO = 0.45;

// Combat micro — focus-fire engagement radius. When an enemy is within this
// many pixels of one of our squads, the bot redirects that squad's order
// toward the enemy's position so CombatSystem's nearest-target acquisition
// naturally picks the enemy we want focused. The number is just over Assault
// range (280) so an Assault that "sees" an enemy via this gate is already
// firing-eligible the same tick.
export const ENGAGE_RADIUS_PX = 320;
export const ENGAGE_RADIUS_SQ = ENGAGE_RADIUS_PX * ENGAGE_RADIUS_PX;

// Recon kite — the gap (in px) between the enemy and the Recon's max firing
// range that triggers a kite. Below this gap the Recon backs off; above it
// the Recon holds position because the enemy can't reach it. 60 px ≈ 0.7 s
// of Assault movement at 90 px/s, which is roughly the AI tick interval
// (200 ms × 4 = 800 ms is the slowest difficulty), so the kite lead is
// always at least one decision window ahead of the enemy closing.
export const RECON_KITE_BUFFER_PX = 60;
// How far the Recon retreats per kite tick. Long enough that a re-fired
// order has somewhere to go (otherwise pathfinder dedupes within 16 px);
// short enough that the Recon stays in firing range of the target it's
// kiting. 180 px ≈ 2.0 s of Recon movement at 110 px/s.
export const RECON_KITE_BACKSTEP_PX = 180;

// Combat micro — focus-fire weighting per archetype. We bias toward the
// lowest-effective-HP enemy so we cash in finishing blows; the bias is
// stronger against high-DPS archetypes (Assault) so we close down their
// damage output first. Squad-cost-weighted so a near-dead Shield still
// outscores a fresh Recon.
const FOCUS_FIRE_THREAT_WEIGHT: Record<SquadArchetype, number> = {
  recon: 1.0,
  engineer: 1.1,
  shield: 1.2,
  assault: 1.5,
};

// Returns the most attractive focus-fire target for `squad` within
// `engagementRangeSq`. Picks lowest *remaining* HP (so we cash finishing
// trades) tie-broken by archetype threat weight (so we end the highest-DPS
// thing first). Pure read; caller decides whether to actually issue an
// attack-move toward the target. Returns null when no enemy is in range.
export function pickFocusFireTarget(
  squad: Squad,
  enemySquads: Squad[],
  engagementRangeSq: number = ENGAGE_RADIUS_SQ,
): Squad | null {
  let best: Squad | null = null;
  let bestScore = Infinity;
  for (const enemy of enemySquads) {
    if (enemy.hp <= 0) continue;
    if (sqDist(enemy.pos, squad.pos) > engagementRangeSq) continue;
    // Lower score = preferred. Effective HP normalised by archetype threat,
    // so an Assault at 200 hp scores LOWER (preferred) than a Recon at 200 hp.
    const threat = FOCUS_FIRE_THREAT_WEIGHT[enemy.archetype];
    const score = enemy.hp / threat;
    if (score < bestScore) {
      bestScore = score;
      best = enemy;
    }
  }
  return best;
}

// Combat micro — pick a safe-pullback anchor for a wounded squad. Prefers a
// healthy ally Shield (so Phase Shield can land); falls back to own Core.
// The returned point is offset slightly so multiple retreating squads don't
// stack on top of the anchor sprite. Returns null when nothing better than
// the squad's current spot exists.
export function pickRetreatTarget(
  squad: Squad,
  ownSquads: Squad[],
  ownCore: Core | null,
): { x: number; y: number } | null {
  let bestShield: Squad | null = null;
  let bestShieldSq = Infinity;
  for (const ally of ownSquads) {
    if (ally.id === squad.id) continue;
    if (ally.archetype !== 'shield') continue;
    if (ally.maxHp <= 0) continue;
    // Only retreat to a Shield that's still combat-effective (so it can
    // actually bubble for us, or at least body-block).
    if (ally.hp / ally.maxHp < 0.45) continue;
    const dSq = sqDist(ally.pos, squad.pos);
    if (dSq < bestShieldSq) {
      bestShieldSq = dSq;
      bestShield = ally;
    }
  }
  if (bestShield !== null) {
    // Stop short of the Shield by ~60 px so we sit inside its 160-px bubble
    // radius (Phase Shield) without overlapping the squad sprite.
    return nudgeToward(squad.pos, bestShield.pos, Math.max(0, Math.sqrt(bestShieldSq) - 60));
  }
  if (ownCore !== null) {
    return nudgeToward(squad.pos, ownCore.pos, Math.sqrt(sqDist(squad.pos, ownCore.pos)) - 60);
  }
  return null;
}

// Combat micro — Recon kite. When an enemy is closing on the Recon AND inside
// (Recon.range - buffer), pick a backstep position directly away from the
// closest such enemy. CombatSystem fires while we move (attackCooldown is
// independent of orderQueue), so the Recon keeps shooting while it backs up.
// Returns null when no enemy is encroaching — caller falls through to its
// normal routing.
//
// V1.5+ overhaul refinement: skip same-or-faster enemies. Kiting only pays
// off when the enemy is slower than us — equal-speed pursuit (Recon-vs-Recon)
// makes both squads walk parallel at the same speed, breaking firing without
// resolving the engagement. Holding position lets the better-positioned unit
// win the trade. Engineer was previously excluded too; the speed-based rule
// folds that exclusion into the same gate (Engineer at 85 px/s IS kite-able,
// but the original "support unit, don't chase it" instinct still applies via
// the focus-fire archetype-weight already preferring Assault/Shield over
// Engineer in target choice).
export function pickKiteTarget(
  recon: Squad,
  enemySquads: Squad[],
  reconRangePx: number,
): { x: number; y: number } | null {
  const reconSpeed = SQUAD_DEFS[recon.archetype].speed;
  const triggerRangePx = reconRangePx - RECON_KITE_BUFFER_PX;
  const triggerRangeSq = triggerRangePx * triggerRangePx;
  let closest: Squad | null = null;
  let closestSq = Infinity;
  for (const enemy of enemySquads) {
    if (enemy.hp <= 0) continue;
    // Speed gate: only kite from enemies strictly slower than us. Equal-speed
    // kites are wasted movement (parallel retreat, no DPS pressure); faster
    // enemies would close the gap regardless (no Squad archetype is faster
    // than Recon today, so this branch is forward-compatible).
    if (SQUAD_DEFS[enemy.archetype].speed >= reconSpeed) continue;
    const dSq = sqDist(enemy.pos, recon.pos);
    if (dSq > triggerRangeSq) continue;
    if (dSq < closestSq) {
      closestSq = dSq;
      closest = enemy;
    }
  }
  if (closest === null) return null;
  // Move directly AWAY from the closest closing enemy. The dx/dy is inverted
  // from nudgeToward, so we hand-build the direction.
  const dx = recon.pos.x - closest.pos.x;
  const dy = recon.pos.y - closest.pos.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) return null;
  const inv = RECON_KITE_BACKSTEP_PX / len;
  return { x: recon.pos.x + dx * inv, y: recon.pos.y + dy * inv };
}

// Combat micro — local-fight loss check. A squad is "outvalued" when the
// summed enemy squad value within LOCAL_FIGHT_RADIUS_PX exceeds the summed
// own-squad value by LOCAL_FIGHT_DISADVANTAGE_RATIO. Used by Commander to
// refuse engagement with a deathball — instead of attack-moving into a 1v3
// fight at a relay, the Commander disengages and looks for chain cuts.
export const LOCAL_FIGHT_RADIUS_PX = 350;
const LOCAL_FIGHT_RADIUS_SQ = LOCAL_FIGHT_RADIUS_PX * LOCAL_FIGHT_RADIUS_PX;
export const LOCAL_FIGHT_DISADVANTAGE_RATIO = 1.6;

export function isLocalFightLosing(
  squad: Squad,
  ownSquads: Squad[],
  enemySquads: Squad[],
): boolean {
  let ourValue = 0;
  let theirValue = 0;
  for (const ally of ownSquads) {
    if (sqDist(ally.pos, squad.pos) > LOCAL_FIGHT_RADIUS_SQ) continue;
    ourValue += squadValue(ally);
  }
  for (const enemy of enemySquads) {
    if (sqDist(enemy.pos, squad.pos) > LOCAL_FIGHT_RADIUS_SQ) continue;
    theirValue += squadValue(enemy);
  }
  if (ourValue <= 0) return theirValue > 0;
  return theirValue / ourValue >= LOCAL_FIGHT_DISADVANTAGE_RATIO;
}

// Adaptive composition: turn an enemy snapshot into target quotas. The base
// 1× Engineer / 1× Shield / 2× Assault profile is the canonical V1 shape; we
// nudge it ±1 by what the enemy is fielding. Sum is held at the V1.5 squadCap
// (4) so the bot doesn't suddenly try to overshoot its limit.
//
// Counter-archetype reads (per docs/02_GAME_DESIGN.md §2 matchup notes):
//   - enemy heavy Assault     → +1 Shield, -1 Recon (Shield front-loads, no
//                                paint utility into pure DPS comp)
//   - enemy heavy Shield      → +1 Recon (Mark Target chips Shield maxHp via
//                                Saboteur AND paint banks +10% Assault dmg),
//                                -1 Shield (a Shield vs a Shield is a slow
//                                trade; we'd rather chip the enemy front)
//   - enemy heavy Recon       → +1 Assault (DPS counter to skirmishers),
//                                -1 Recon
//   - enemy heavy Engineer    → +1 Assault (turret pressure)
//
// Determinism: pure function of the enemy snapshot. No RNG (Rule 8).
export function adaptiveCompositionQuota(
  enemySquads: Squad[],
  squadCap: number,
): Record<SquadArchetype, number> {
  const counts: Record<SquadArchetype, number> = { recon: 0, assault: 0, engineer: 0, shield: 0 };
  for (const sq of enemySquads) counts[sq.archetype] += 1;
  const total = counts.recon + counts.assault + counts.engineer + counts.shield;
  // Defaults: 1 engineer + 1 shield + 2 assault = 4 (canonical V1 cap).
  const quota: Record<SquadArchetype, number> = { recon: 0, engineer: 1, shield: 1, assault: 2 };
  if (total === 0) return clampQuota(quota, squadCap);
  // Threshold: if any archetype is ≥ 50% of the enemy's fielded squads,
  // treat it as "heavy". Below that we keep the default profile.
  const heavyShare = 0.5;
  if (counts.assault / total >= heavyShare) {
    quota.shield += 1;
    quota.assault -= 1; // re-balance so we don't overshoot the cap
  } else if (counts.shield / total >= heavyShare) {
    quota.recon += 1;
    quota.shield = Math.max(0, quota.shield - 1);
  } else if (counts.recon / total >= heavyShare) {
    quota.assault += 1;
    quota.shield = Math.max(0, quota.shield - 1);
  } else if (counts.engineer / total >= heavyShare) {
    quota.assault += 1;
    quota.engineer = Math.max(0, quota.engineer - 1);
  }
  return clampQuota(quota, squadCap);
}

function clampQuota(
  quota: Record<SquadArchetype, number>,
  squadCap: number,
): Record<SquadArchetype, number> {
  // Sum can drift one off when the counter rule subtracts a 0; floor at 0 and
  // re-balance against the cap. Priority order on overflow trim:
  // recon → engineer → shield → assault (assault is the bot's DPS spine,
  // trimmed last).
  const order: SquadArchetype[] = ['recon', 'engineer', 'shield', 'assault'];
  for (const a of order) if (quota[a] < 0) quota[a] = 0;
  let sum = quota.recon + quota.engineer + quota.shield + quota.assault;
  let i = 0;
  while (sum > squadCap && i < order.length) {
    const a = order[i]!;
    if (quota[a] > 0) {
      quota[a] -= 1;
      sum -= 1;
    } else {
      i += 1;
    }
  }
  return quota;
}

// Scouting target — pick an exploration objective for a Recon early game.
// Prefers a relay where ownership is currently null (we'd be capturing AND
// scouting), tie-broken by distance from any enemy turret cluster. The §17
// anti-turtling story stays intact because once the Recon arrives it just
// captures normally; this only changes the *first leg* of the Recon's path
// when no abilities are warranted. Returns null when every relay is
// already owned (mid-late game) — caller falls through to pickRelayTarget.
export function pickScoutTarget(
  snap: PlayerView,
  recon: Squad,
  ownerId: PlayerId,
): { x: number; y: number } | null {
  // Pre-collect enemy turret positions for the avoidance term — same gate
  // as pickRelayTarget so the Recon doesn't scout into a 1v3.
  const enemyTurrets: { x: number; y: number }[] = [];
  for (const b of Object.values(snap.buildings)) {
    if (b.ownerId === ownerId) continue;
    if (b.kind !== 'turret' && b.kind !== 'mini_turret') continue;
    if (!isThreatBuilding(b)) continue;
    enemyTurrets.push(b.pos);
  }
  let best: { x: number; y: number } | null = null;
  let bestScore = Infinity;
  for (const node of Object.values(snap.controlNodes)) {
    if (node.ownerId !== null) continue; // owned: not a scout target
    if (node.guardedBy === 'neutral_camp') continue; // suicidal for Recon
    let score = sqDist(node.pos, recon.pos);
    for (const t of enemyTurrets) {
      if (sqDist(node.pos, t) <= TURRET_AVOID_PX * TURRET_AVOID_PX) {
        score += 5_000_000;
      }
    }
    if (score < bestScore) {
      bestScore = score;
      best = { x: node.pos.x, y: node.pos.y };
    }
  }
  return best;
}

// V1.5+ overhaul — safe-side channel point for the anti-deathball read.
// The naive implementation walks Recon to the support relay's center, which
// is exactly where the turret cluster lives — the Recon dies before the
// 3 s Cut Link channel can land. This helper picks a position 200 px from
// the relay (just inside Cut Link's 220 px range) on the far side of the
// turret cluster, so the Recon ends the walk in channel range AND with the
// relay itself between it and the densest turret fire. Returns null when
// no support relay exists. Pure read; no mutation.
export function pickSupportRelayChannelPoint(
  snap: PlayerView,
  ownerId: PlayerId,
): { id: EntityId; pos: { x: number; y: number } } | null {
  const relay = pickSupportRelayToSnipe(snap, ownerId);
  if (relay === null) return null;
  const enemyId: PlayerId = (ownerId === 0 ? 1 : 0) as PlayerId;
  // Collect the anchored turrets we want to stay outside of.
  let cx = 0;
  let cy = 0;
  let count = 0;
  for (const b of Object.values(snap.buildings)) {
    if (b.kind !== 'mini_turret') continue;
    if (b.ownerId !== enemyId) continue;
    if (!b.isOnline) continue;
    if (sqDist(b.pos, { x: relay.x, y: relay.y }) > FORWARD_TURRET_RANGE_SQ) continue;
    cx += b.pos.x;
    cy += b.pos.y;
    count += 1;
  }
  // No anchored turrets visible (degenerate — pickSupportRelayToSnipe gates
  // on anchored ≥ 1, so this only fires on a stale snapshot). Walk the
  // relay center.
  if (count === 0) {
    return { id: relay.id, pos: { x: relay.x, y: relay.y } };
  }
  cx /= count;
  cy /= count;
  // Direction FROM turret centroid TO relay (= toward the far side of the
  // relay from the cluster). Continue PAST the relay along that direction
  // so the Recon ends up on the side OPPOSITE the cluster.
  const dx = relay.x - cx;
  const dy = relay.y - cy;
  const len = Math.hypot(dx, dy);
  // Degenerate: cluster centred exactly on relay (e.g., one turret at the
  // relay). No safe side — return relay center; we're committed to the play.
  if (len < 1) {
    return { id: relay.id, pos: { x: relay.x, y: relay.y } };
  }
  // 200 px from relay on the safe side, well inside Cut Link's 220 px target
  // range so the channel actually lands.
  const SAFE_CHANNEL_OFFSET_PX = 200;
  const inv = SAFE_CHANNEL_OFFSET_PX / len;
  return {
    id: relay.id,
    pos: { x: relay.x + dx * inv, y: relay.y + dy * inv },
  };
}

// V1.5+ "snipe-the-support-relay" hook for the anti-deathball read.
// When Commander is army-disadvantaged and the enemy is deathballing, it
// redirects its Recon to cut the relay anchoring the enemy's forward turret
// nest. Returns the relay closest to the largest enemy turret cluster (the
// one whose cut would dim the most forward firepower). Null if no enemy
// forward turret exists yet — there's no support relay to snipe.
export function pickSupportRelayToSnipe(
  snap: PlayerView,
  ownerId: PlayerId,
): { id: EntityId; x: number; y: number } | null {
  const enemyId: PlayerId = (ownerId === 0 ? 1 : 0) as PlayerId;
  const enemyTurrets: { x: number; y: number }[] = [];
  for (const b of Object.values(snap.buildings)) {
    if (b.kind !== 'mini_turret') continue;
    if (b.ownerId !== enemyId) continue;
    if (!b.isOnline) continue;
    enemyTurrets.push(b.pos);
  }
  if (enemyTurrets.length === 0) return null;
  let best: { id: EntityId; x: number; y: number } | null = null;
  let bestAnchored = 0;
  let bestDistSq = Infinity;
  for (const node of Object.values(snap.controlNodes)) {
    if (node.ownerId !== enemyId) continue;
    // Already cut → not useful to redirect; the dim is already paid for.
    if (snap.tick < node.linkCutUntilTick) continue;
    let anchored = 0;
    let minDistSq = Infinity;
    for (const t of enemyTurrets) {
      const d = sqDist(node.pos, t);
      if (d <= FORWARD_TURRET_RANGE_SQ) anchored += 1;
      if (d < minDistSq) minDistSq = d;
    }
    if (anchored === 0) continue;
    // Prefer the relay anchoring the most turrets; tie-break on the relay
    // closest to the densest turret cluster (so the Recon's commute is short).
    if (anchored > bestAnchored || (anchored === bestAnchored && minDistSq < bestDistSq)) {
      bestAnchored = anchored;
      bestDistSq = minDistSq;
      best = { id: node.id, x: node.pos.x, y: node.pos.y };
    }
  }
  return best;
}

// Compose-priority: walk an archetype priority list, return the first archetype
// whose alive+queued count is below quota. Wait if we can't afford the next slot —
// buying a Recon to fill the cap with the wrong archetype is worse than waiting.
export function pickNextArchetype(
  ownSquads: Squad[],
  queued: SquadArchetype[],
  signalAvailable: number,
  order: SquadArchetype[],
  quota: Record<SquadArchetype, number>,
): SquadArchetype | null {
  const have: Record<SquadArchetype, number> = { recon: 0, assault: 0, engineer: 0, shield: 0 };
  for (const sq of ownSquads) have[sq.archetype] += 1;
  for (const a of queued) have[a] += 1;
  for (const want of order) {
    if (have[want] >= quota[want]) continue;
    if (signalAvailable < SQUAD_DEFS[want].cost) return null;
    return want;
  }
  return null;
}

export function pickFreeSlot(snap: PlayerView, core: Core, preferred: number): number | null {
  if (preferred >= 0 && preferred < BUILD_SLOTS_PER_CORE && !isSlotOccupied(snap, core.id, preferred)) {
    return preferred;
  }
  for (let i = 0; i < BUILD_SLOTS_PER_CORE; i++) {
    if (!isSlotOccupied(snap, core.id, i)) return i;
  }
  return null;
}

// V1.5+ forward turret network dependency. Centralised "is this enemy
// building actually a threat right now?" gate — bots use it both for routing
// (avoid live turret coverage) and for target selection. Offline mini-turrets
// return false: zero threat weight, so a Recon walking past one isn't
// pushed off course, and a Commander treating it as an attack target gets
// the cheap-objective bonus instead of the live-turret penalty. Permanent
// turrets and production hubs are always threats from the bot's perspective.
export function isThreatBuilding(b: Building): boolean {
  if (b.kind === 'mini_turret' && !b.isOnline) return false;
  return true;
}

// V1.5+ Slice 6 — find an enemy relay where Disrupt Uplink would actually
// do something (i.e., an enemy forward `mini_turret` is anchored on it,
// within FORWARD_TURRET_RANGE_PX). Returns null if no such relay exists in
// DISRUPT_UPLINK_TARGET_RANGE_PX from the squad. Per Rule 18 — disruption
// MUST be felt on the battlefield, so the bot should only spend the channel
// commit when there's a turret to degrade. A bot disrupting an empty relay
// is wasting a 2.5 s channel + 40 s cooldown for no observable effect.
//
// Skips:
//   - own relays (degrading our own turrets via opposite-player check is a no-op)
//   - relays already in a disruption window (the disrupt would just refresh)
//   - relays with no anchored enemy forward turret (no felt effect)
//
// Read-only: never mutates state. Caller emits an AbilityCommand if non-null.
export function findDisruptableRelay(
  snap: PlayerView,
  squad: Squad,
  ownerId: PlayerId,
): ControlNode | null {
  const enemyId: PlayerId = (ownerId === 0 ? 1 : 0) as PlayerId;
  // Pre-collect enemy forward turrets (online or offline — disruption is
  // worth committing pre-emptively if the chain is up but the turret is
  // currently dark; the §17 gate cares about "would forward firepower be
  // present?", and a turret currently offline because of an upstream cut
  // can come back online any tick when the cut expires).
  const enemyTurrets: { x: number; y: number }[] = [];
  for (const b of Object.values(snap.buildings)) {
    if (b.kind !== 'mini_turret') continue;
    if (b.ownerId !== enemyId) continue;
    enemyTurrets.push(b.pos);
  }
  if (enemyTurrets.length === 0) return null;
  let best: ControlNode | null = null;
  let bestScore = -Infinity;
  for (const node of Object.values(snap.controlNodes)) {
    if (node.ownerId === ownerId) continue;
    if (sqDist(squad.pos, node.pos) > DISRUPT_UPLINK_TARGET_RANGE_SQ) continue;
    if (snap.tick < node.disruptedUntilTick) continue;
    let anchored = 0;
    for (const t of enemyTurrets) {
      if (sqDist(node.pos, t) <= FORWARD_TURRET_RANGE_SQ) anchored += 1;
    }
    if (anchored === 0) continue;
    // More anchored turrets → higher value disruption (one cast hits multiple
    // forward firepower sources). Tie-break on enemy ownership (a relay the
    // enemy actively holds is a more legitimate forward position than a
    // neutral relay an enemy turret happens to overlap).
    const score = anchored * 1000 + (node.ownerId === enemyId ? 100 : 0);
    if (score > bestScore) {
      bestScore = score;
      best = node;
    }
  }
  return best;
}

// V1.5+ Slice 7 — find a relay where Fortify Relay would actually do
// something. Returns null if no such relay exists in
// FORTIFY_RELAY_TARGET_RANGE_PX from the squad. Per Rule 18 — defensive
// abilities must also be felt on the battlefield, so the bot only spends
// the channel commit when there's a measurable effect to land:
//   (a) an own forward `mini_turret` is anchored on the relay (within
//       FORWARD_TURRET_RANGE_PX) — the +25% fire-rate buff has
//       somewhere to land. The relay's OWNERSHIP doesn't matter here:
//       the fire-rate buff filters by `fortifiedByPlayerId ===
//       turret.ownerId`, not by relay owner, so an own turret near a
//       contested or enemy-held relay still benefits from a fortify
//       cast on that relay.
//   (b) the relay is contested OR an enemy squad is within
//       FORTIFY_RELAY_THREAT_RANGE_SQ (an attack is plausibly imminent —
//       a fortify here either deflects an incoming cut/jam/disrupt
//       channel via the immunity property OR buffs the own anchored
//       turret in time for the trade).
//
// Skips:
//   - relays already in a fortify window (the fortify would just refresh)
//   - relays without an anchored own mini_turret (no felt effect; the
//     immunity alone isn't worth a 50 s cooldown for a bot)
//   - quiet relays (no enemy presence — fortifying a safe rear relay
//     would be the §17 anti-turtling failure mode)
//
// The §17 gate is what this heuristic is calibrated against: a bot that
// fortifies eagerly on safe relays tanks the relay-flip rate; a bot that
// fortifies only under threat keeps the rate within ±10% of the
// post-Slice-6 baseline.
//
// Read-only: never mutates state. Caller emits an AbilityCommand if non-null.
export function findFortifyableRelay(
  snap: PlayerView,
  squad: Squad,
  ownerId: PlayerId,
): ControlNode | null {
  const enemyId: PlayerId = (ownerId === 0 ? 1 : 0) as PlayerId;
  // Pre-collect own forward mini-turrets so we can ask "is there at least
  // one anchored on this candidate relay?" in O(turrets) per relay.
  const ownTurrets: { x: number; y: number }[] = [];
  for (const b of Object.values(snap.buildings)) {
    if (b.kind !== 'mini_turret') continue;
    if (b.ownerId !== ownerId) continue;
    ownTurrets.push(b.pos);
  }
  if (ownTurrets.length === 0) return null;
  // Pre-collect enemy squad positions for the threat check. Only enemies
  // visible in the bot's fog count — the bot doesn't get omniscience
  // here (Rule 18: bots emit Commands like humans, no x-ray).
  const enemyPositions: { x: number; y: number }[] = [];
  for (const sq of Object.values(snap.squads)) {
    if (sq.ownerId !== enemyId) continue;
    enemyPositions.push(sq.pos);
  }
  // Threat range is wider than the disrupt range: a Shield is the slowest
  // archetype, so it must commit BEFORE the enemy reaches the relay (not
  // after). 350 px gives ~3.9 s of warning at Assault speed (90 px/s),
  // long enough for the 2.5 s channel to land.
  const threatRangeSq = FORTIFY_RELAY_THREAT_RANGE_PX * FORTIFY_RELAY_THREAT_RANGE_PX;
  let best: ControlNode | null = null;
  let bestScore = -Infinity;
  for (const node of Object.values(snap.controlNodes)) {
    if (sqDist(squad.pos, node.pos) > FORTIFY_RELAY_TARGET_RANGE_SQ) continue;
    if (snap.tick < node.fortifiedUntilTick) continue;
    let anchored = 0;
    for (const t of ownTurrets) {
      if (sqDist(node.pos, t) <= FORWARD_TURRET_RANGE_SQ) anchored += 1;
    }
    if (anchored === 0) continue;
    // Threat gate: at least one enemy squad within
    // FORTIFY_RELAY_THREAT_RANGE_PX OR the relay is currently contested.
    // Without this, the bot would happily fortify a safe rear relay and
    // tank the §17 flip rate.
    let threatened = node.contested;
    if (!threatened) {
      for (const e of enemyPositions) {
        if (sqDist(node.pos, e) <= threatRangeSq) {
          threatened = true;
          break;
        }
      }
    }
    if (!threatened) continue;
    // Score: more anchored turrets → more buff value. Tie-break on
    // contested (a contested relay is the most-load-bearing place to
    // fortify — both immunity AND buff matter). Own relays score
    // slightly higher than non-own (the strategic story is "defend my
    // forward position" first; cross-relay fortifies are a fallback).
    let score = anchored * 1000;
    if (node.contested) score += 500;
    if (node.ownerId === ownerId) score += 200;
    if (score > bestScore) {
      bestScore = score;
      best = node;
    }
  }
  return best;
}

// V1.5+ Slice 7 — Shield's threat-detection radius. Wider than the
// disrupt range because a Shield is the slowest archetype and must commit
// BEFORE the enemy reaches the relay. 350 px ≈ 3.9 s of warning at
// Assault speed (90 px/s) — comfortably above the 2.5 s channel time.
export const FORTIFY_RELAY_THREAT_RANGE_PX = 350;

export function countOwnBuildings(
  snap: PlayerView,
  playerId: PlayerId,
  kind: Building['kind'],
): number {
  let n = 0;
  for (const b of Object.values(snap.buildings)) {
    if (b.ownerId !== playerId) continue;
    if (b.kind !== kind) continue;
    n += 1;
  }
  return n;
}

export function isAtContestedRelay(snap: PlayerView, squad: Squad): boolean {
  // Relay capture radius is 120 px (CaptureSystem.CAPTURE_RADIUS_PX). Use a
  // slightly larger window so the bot drops the turret right as it walks in,
  // not after it's already standing on the dot.
  const radius = 140;
  const radiusSq = radius * radius;
  for (const node of Object.values(snap.controlNodes)) {
    if (sqDist(node.pos, squad.pos) > radiusSq) continue;
    if (node.contested) return true;
  }
  return false;
}

// Pick a relay target for a squad. Engineers prioritize closest non-owned;
// Shield/Assault/Recon prefer relays we own or lead (consolidation), then mid.
// Recon avoids known enemy turret radii so it doesn't pop into firing range.
//
// `bias` lets a tier nudge the score: Commander uses 'capture' under own surge
// (favor neutral / enemy relays harder) and 'pressure' under enemy surge (favor
// the leader's relays — consume their lead before the surge shrinks the gap).
export type RelayBias = 'default' | 'capture' | 'pressure_leader';

export function pickRelayTarget(
  snap: PlayerView,
  squad: Squad,
  ownerId: PlayerId,
  ownCore: Core | null,
  enemySquads: Squad[],
  bias: RelayBias = 'default',
): { x: number; y: number } | null {
  const enemyTurrets: { x: number; y: number }[] = [];
  for (const b of Object.values(snap.buildings)) {
    if (b.ownerId === ownerId) continue;
    if (b.kind !== 'turret' && b.kind !== 'mini_turret') continue;
    // V1.5+ forward turret network dependency. An offline mini-turret is
    // inert — no firing, no vision. Treating it as a threat would have Recon
    // route around an empty husk; instead let it pass through and become a
    // free objective (cheap to destroy, denies the enemy a future
    // reactivation). Permanent turrets are kept always-on by
    // ForwardTurretNetworkSystem so this only filters mini_turrets.
    if (!isThreatBuilding(b)) continue;
    enemyTurrets.push(b.pos);
  }
  const enemyId: PlayerId = (ownerId === 0 ? 1 : 0) as PlayerId;
  // Leader = whoever holds strictly more relays right now. Used only for the
  // pressure_leader bias; null if the count is tied.
  let leaderId: PlayerId | null = null;
  if (bias === 'pressure_leader') {
    let ownNodes = 0;
    let enemyNodes = 0;
    for (const node of Object.values(snap.controlNodes)) {
      if (node.ownerId === ownerId) ownNodes += 1;
      else if (node.ownerId === enemyId) enemyNodes += 1;
    }
    if (ownNodes > enemyNodes) leaderId = ownerId;
    else if (enemyNodes > ownNodes) leaderId = enemyId;
  }
  let best: { node: ControlNode; score: number } | null = null;
  for (const node of Object.values(snap.controlNodes)) {
    const ownDist = ownCore === null ? 0 : Math.sqrt(sqDist(node.pos, ownCore.pos));
    const sqDistToSquad = sqDist(node.pos, squad.pos);
    let score = sqDistToSquad + ownDist * ownDist * 0.25;
    if (squad.archetype === 'engineer') {
      if (node.ownerId === ownerId) score += 1_000_000;
    } else if (squad.archetype === 'recon') {
      if (node.ownerId !== null && node.ownerId !== ownerId) score += 250_000;
      for (const t of enemyTurrets) {
        if (sqDist(node.pos, t) <= TURRET_AVOID_PX * TURRET_AVOID_PX) {
          score += 5_000_000;
        }
      }
    } else {
      if (node.ownerId === ownerId) score += 100_000;
    }
    // V1.5 neutral camp: heavily penalize an active-camp relay so the bot
    // doesn't suicide its squads into the patrolling guards. Recon eats the
    // largest penalty (a Recon is paste in camp range); other archetypes pay
    // a smaller cost so a Shield/Assault frontline can still clear the camp
    // when there's no better target.
    if (node.guardedBy === 'neutral_camp') {
      score += squad.archetype === 'recon' ? 6_000_000 : 800_000;
    }
    if (bias === 'capture') {
      // Heavy bonus for not-owned-by-us relays; penalize own (we already have it).
      if (node.ownerId === ownerId) score += 500_000;
      else score -= 250_000;
    } else if (bias === 'pressure_leader' && leaderId === enemyId) {
      // Drag the squad toward the leader's relays so we consume the surge gap.
      if (node.ownerId === enemyId) score -= 750_000;
    }
    let enemyNear = 0;
    for (const e of enemySquads) {
      if (sqDist(e.pos, node.pos) <= 200 * 200) enemyNear += 1;
    }
    if (enemyNear >= 2 && squad.archetype === 'recon') score += 1_500_000;
    // V1.5+ telemetry pre-flight: deterministic-per-seed jitter so identical
    // snapshots from different state.seed values break ties differently. Hash
    // inputs: snap.seed (varies per match), snap.tick (varies per AI tick
    // within a match), node.id (so two relays scored at the same tick get
    // different jitters). Stateless — does NOT mutate state.rng (Rule 1).
    // Replay determinism is preserved: same seed + same tick + same node
    // always yields the same jitter.
    const jitter = (stableHash(snap.seed, snap.tick, node.id) % (PICK_RELAY_JITTER_MAGNITUDE * 2))
      - PICK_RELAY_JITTER_MAGNITUDE;
    score += jitter;
    if (best === null || score < best.score) best = { node, score };
  }
  return best === null ? null : { x: best.node.pos.x, y: best.node.pos.y };
}
